import dayjs from '../../../utils/dayjs';

export const getOrgIdForSessionName = (userInfo) => {
  const rawOrgId =
    userInfo?.businessOrganisationIdentifier ||
    userInfo?.businessOrganisationId ||
    userInfo?.organisationId ||
    'ORG';

  const normalized = String(rawOrgId).replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return normalized || 'ORG';
};

export const UPLOAD_SESSION_NAME_SETTING_KEY = 'UploadSessionNameConfig';

/** Variables available in the pattern builder. Legacy keys in saved configs still resolve. */
export const SESSION_NAME_VARIABLES = {
  PREFIX: { labelKey: 'sessionName.var.prefix', token: 'PREFIX', example: 'SESSION' },
  ORG_ID: { labelKey: 'sessionName.var.orgId', token: 'ORG_ID', example: 'ORG123' },
  DATE: { labelKey: 'sessionName.var.date', token: 'DATE', example: '2026-06-10' },
  DAY_NUM: { labelKey: 'sessionName.var.dayNum', token: 'DD', example: '10' },
  MONTH_NUM: { labelKey: 'sessionName.var.monthNum', token: 'MM', example: '06' },
  MONTH_SHORT: { labelKey: 'sessionName.var.monthShort', token: 'MMM', example: 'Jun' },
  YEAR: { labelKey: 'sessionName.var.year', token: 'YYYY', example: '2026' },
  YEAR_SHORT: { labelKey: 'sessionName.var.yearShort', token: 'YY', example: '26' },
  WEEK: { labelKey: 'sessionName.var.week', token: 'W24', example: 'W24' },
  QUARTER: { labelKey: 'sessionName.var.quarter', token: 'Q2', example: 'Q2' },
  FORTNIGHT: { labelKey: 'sessionName.var.fortnight', token: 'F12', example: 'F12' },
  HALF_YEAR: { labelKey: 'sessionName.var.halfYear', token: 'H1', example: 'H1' },
};

const KNOWN_SEGMENT_KEYS = new Set([
  ...Object.keys(SESSION_NAME_VARIABLES),
  // legacy keys from earlier configs
  'MONTH_NUM_SHORT', 'MONTH_LONG', 'WEEK_YEAR', 'FORTNIGHT_YEAR', 'QUARTER_YEAR',
  'HALF_YEAR_YEAR', 'BIMONTH', 'BIMONTH_YEAR', 'FOURWEEK', 'FOURWEEK_YEAR', 'PERIOD_KEY',
]);

export const getVariableToken = (key) =>
  SESSION_NAME_VARIABLES[key]?.token || key;

export const DEFAULT_SESSION_NAME_CONFIG = {
  enabled: false,
  prefix: 'SESSION',
  separator: '-',
  segments: [
    { type: 'variable', key: 'PREFIX' },
    { type: 'variable', key: 'MONTH_NUM' },
    { type: 'variable', key: 'YEAR' },
    { type: 'variable', key: 'ORG_ID' },
  ],
};

let cachedConfig = { ...DEFAULT_SESSION_NAME_CONFIG };

export const setSessionNameConfigCache = (config) => {
  cachedConfig = normalizeSessionNameConfig(config);
};

export const getSessionNameConfigCache = () => cachedConfig;

const getFortnightMeta = (ref) => {
  const isoWeek = ref.isoWeek();
  const startWeek = isoWeek % 2 === 0 ? isoWeek - 1 : isoWeek;
  const start = ref.isoWeek(startWeek).startOf('isoWeek');
  const end = start.add(13, 'day').endOf('day');
  const index = Math.ceil(isoWeek / 2);
  return { start, end, label: `F${index}` };
};

const getSemiannualMeta = (ref) => {
  const half = ref.month() < 6 ? 1 : 2;
  const start = half === 1 ? ref.startOf('year') : ref.month(6).startOf('month');
  const end = half === 1 ? ref.month(5).endOf('month') : ref.endOf('year');
  return { start, end, label: `H${half}` };
};

export const normalizeSessionNameConfig = (config) => {
  const base = { ...DEFAULT_SESSION_NAME_CONFIG, ...(config || {}) };
  const segments = Array.isArray(base.segments) && base.segments.length > 0
    ? base.segments
        .filter((s) => s?.type === 'variable' && KNOWN_SEGMENT_KEYS.has(s.key))
        .map((s) => ({ type: 'variable', key: s.key }))
    : [...DEFAULT_SESSION_NAME_CONFIG.segments];

  return {
    enabled: Boolean(base.enabled),
    prefix: String(base.prefix || 'SESSION').trim() || 'SESSION',
    separator: base.separator === '' ? '' : (base.separator ?? '-'),
    segments,
  };
};

export const parseSessionNameConfig = (settingValue) => {
  if (!settingValue) return { ...DEFAULT_SESSION_NAME_CONFIG };
  try {
    const parsed = typeof settingValue === 'string' ? JSON.parse(settingValue) : settingValue;
    return normalizeSessionNameConfig(parsed);
  } catch {
    return { ...DEFAULT_SESSION_NAME_CONFIG };
  }
};

export const serializeSessionNameConfig = (config) =>
  JSON.stringify(normalizeSessionNameConfig(config));

const resolveVariable = (key, { userInfo, referenceDate, config }) => {
  const ref = dayjs(referenceDate);
  const orgId = getOrgIdForSessionName(userInfo);
  const fortnight = getFortnightMeta(ref);
  const semiannual = getSemiannualMeta(ref);

  switch (key) {
    case 'PREFIX': return config.prefix;
    case 'ORG_ID': return orgId;
    case 'DATE': return ref.format('YYYY-MM-DD');
    case 'DAY_NUM': return ref.format('DD');
    case 'MONTH_NUM': return ref.format('MM');
    case 'MONTH_NUM_SHORT': return ref.format('M');
    case 'MONTH_SHORT': return ref.format('MMM');
    case 'MONTH_LONG': return ref.format('MMMM');
    case 'YEAR': return ref.format('YYYY');
    case 'YEAR_SHORT': return ref.format('YY');
    case 'WEEK': return `W${String(ref.isoWeek()).padStart(2, '0')}`;
    case 'WEEK_YEAR': return `${ref.isoWeekYear()}-W${String(ref.isoWeek()).padStart(2, '0')}`;
    case 'FORTNIGHT': return fortnight.label;
    case 'FORTNIGHT_YEAR': return `${fortnight.label}-${ref.isoWeekYear()}`;
    case 'QUARTER': return `Q${ref.quarter()}`;
    case 'QUARTER_YEAR': return `Q${ref.quarter()}-${ref.format('YYYY')}`;
    case 'HALF_YEAR': return semiannual.label;
    case 'HALF_YEAR_YEAR': return `${semiannual.label}-${ref.year()}`;
    case 'BIMONTH': return `B${Math.ceil((ref.month() + 1) / 2)}`;
    case 'BIMONTH_YEAR': return `B${Math.ceil((ref.month() + 1) / 2)}-${ref.year()}`;
    case 'FOURWEEK': {
      const period = Math.floor(ref.diff(ref.startOf('year'), 'day') / 28) + 1;
      return `4W${String(period).padStart(2, '0')}`;
    }
    case 'FOURWEEK_YEAR': {
      const period = Math.floor(ref.diff(ref.startOf('year'), 'day') / 28) + 1;
      return `4W${String(period).padStart(2, '0')}-${ref.year()}`;
    }
    case 'PERIOD_KEY': return ref.format('MM-YYYY');
    default: return '';
  }
};

export const buildSessionNameFromConfig = (config, userInfo, referenceDate = dayjs()) => {
  const normalized = normalizeSessionNameConfig(config);
  if (!normalized.enabled) {
    return buildDefaultSessionName(userInfo, referenceDate);
  }

  return normalized.segments
    .map((s) => resolveVariable(s.key, { userInfo, referenceDate, config: normalized }))
    .filter((part) => part != null && String(part).length > 0)
    .join(normalized.separator);
};

export const buildDefaultSessionName = (userInfo, referenceDate = dayjs()) => {
  const orgId = getOrgIdForSessionName(userInfo);
  return `SESSION-${dayjs(referenceDate).format('MM-YYYY')}-${orgId}`;
};

/** Infer cohort study window from the finest time variable in the pattern. */
const inferPeriodRange = (segments, ref) => {
  const keys = new Set(segments.map((s) => s.key));

  if (keys.has('DATE') || keys.has('DAY_NUM')) {
    return { start: ref.startOf('day'), end: ref.endOf('day') };
  }
  if (keys.has('WEEK') || keys.has('WEEK_YEAR')) {
    return { start: ref.startOf('isoWeek'), end: ref.endOf('isoWeek') };
  }
  if (keys.has('FORTNIGHT') || keys.has('FORTNIGHT_YEAR')) {
    const { start, end } = getFortnightMeta(ref);
    return { start, end };
  }
  if (keys.has('QUARTER') || keys.has('QUARTER_YEAR')) {
    return { start: ref.startOf('quarter'), end: ref.endOf('quarter') };
  }
  if (keys.has('HALF_YEAR') || keys.has('HALF_YEAR_YEAR')) {
    const { start, end } = getSemiannualMeta(ref);
    return { start, end };
  }
  if (keys.has('YEAR') && !keys.has('MONTH_NUM') && !keys.has('MONTH_SHORT') && !keys.has('MONTH_LONG')) {
    return { start: ref.startOf('year'), end: ref.endOf('year') };
  }
  return { start: ref.startOf('month'), end: ref.endOf('month') };
};

export const getSessionPeriodRange = (config, referenceDate = dayjs()) => {
  const normalized = normalizeSessionNameConfig(config);
  const ref = dayjs(referenceDate);
  const { start, end } = normalized.enabled
    ? inferPeriodRange(normalized.segments, ref)
    : { start: ref.startOf('month'), end: ref.endOf('month') };

  return {
    studyStartDate: start.toISOString(),
    studyEndDate: end.toISOString(),
  };
};

export const getDefaultSegments = () => [...DEFAULT_SESSION_NAME_CONFIG.segments];
